import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    // Charger les plans depuis l'API
    const fetchPlans = async () => {
      try {
        const response = await axios.get('/api/abonnements/plans');
        const plansData = response.data.map((plan, index) => ({
          name: plan.nom,
          price: plan.prixMensuel.toLocaleString('fr-FR'),
          period: 'mois',
          description: plan.description || `Idéal pour ${plan.nom.toLowerCase()}`,
          features: plan.features || [
            `${plan.limites.boutiques === 999999 ? 'Boutiques illimitées' : plan.limites.boutiques + ' boutique(s)'}`,
            `${plan.limites.utilisateurs === 999999 ? 'Utilisateurs illimités' : plan.limites.utilisateurs + ' utilisateur(s)'}`,
            'Gestion des stocks',
            'Point de vente (POS)',
            'Génération de factures'
          ],
          highlighted: plan.nom === 'Pro',
          color: plan.nom === 'Starter' ? '#3498db' : plan.nom === 'Pro' ? '#2ecc71' : '#9b59b6'
        }));
        setPlans(plansData);
      } catch (error) {
        console.log('Erreur lors du chargement des plans, utilisation des plans par défaut');
        // Plans par défaut si l'API échoue
        setPlans([
          {
            name: 'Starter',
            price: '100 000',
            period: 'mois',
            description: 'Idéal pour démarrer',
            features: ['1 boutique', '2 utilisateurs', 'Gestion des stocks', 'Point de vente', 'Support email'],
            highlighted: false,
            color: '#3498db'
          },
          {
            name: 'Pro',
            price: '150 000',
            period: 'mois',
            description: 'Pour les entreprises en croissance',
            features: ['3 boutiques', '5 utilisateurs', 'Dashboard avancé', 'Statistiques détaillées', 'Support prioritaire'],
            highlighted: true,
            color: '#2ecc71'
          },
          {
            name: 'Entreprise',
            price: '300 000',
            period: 'mois',
            description: 'Solution complète',
            features: ['Illimité boutiques', 'Illimité utilisateurs', 'API personnalisée', 'Support 24/7'],
            highlighted: false,
            color: '#9b59b6'
          }
        ]);
      }
    };
    
    fetchPlans();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const advantages = [
    {
      icon: '🏪',
      title: 'Multi-boutiques',
      description: 'Gérez plusieurs points de vente depuis une seule plateforme centralisée'
    },
    {
      icon: '📊',
      title: 'Statistiques en temps réel',
      description: 'Suivez vos ventes, stocks et performances en temps réel avec des tableaux de bord intuitifs'
    },
    {
      icon: '🔒',
      title: 'Sécurisé',
      description: 'Isolation complète des données avec architecture multi-tenant de niveau entreprise'
    },
    {
      icon: '💳',
      title: 'Point de vente moderne',
      description: 'Interface caisse rapide et intuitive avec support de multiples modes de paiement'
    },
    {
      icon: '📱',
      title: 'Accessible partout',
      description: 'Application web responsive accessible depuis n\'importe quel appareil'
    },
    {
      icon: '⚡',
      title: 'Rapide et performant',
      description: 'Infrastructure optimisée pour des performances exceptionnelles même avec de gros volumes'
    }
  ];

  return (
    <div className="homepage">
      {/* Navigation Bar */}
      <nav className="navbar" style={{ background: scrollY > 50 ? 'rgba(255, 255, 255, 0.98)' : 'transparent' }}>
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-icon">📊</span>
            <span className="logo-text">gestStock</span>
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">Fonctionnalités</a>
            <a href="#pricing" className="nav-link">Tarifs</a>
            <a href="#about" className="nav-link">À propos</a>
            <button className="nav-btn-login" onClick={() => navigate('/login')}>Connexion</button>
            <button className="nav-btn-primary" onClick={() => navigate('/register')}>Essai gratuit</button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Simplifié */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="gradient-overlay"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <span>🚀 Solution SaaS #1 au Sénégal</span>
          </div>
          
          <h1 className="hero-title">
            Transformez votre
            <span className="gradient-text"> commerce </span>
            avec l'intelligence numérique
          </h1>
          
          <p className="hero-subtitle">
            La plateforme tout-en-un qui révolutionne la gestion de votre entreprise.
            <br />Multi-boutiques • Temps réel • Sécurisé • Performant
          </p>
          
          <div className="hero-cta">
            <button className="btn-hero-primary" onClick={() => navigate('/register')}>
              <span>Démarrer gratuitement</span>
              <span className="btn-arrow">→</span>
            </button>
            <button className="btn-hero-secondary" onClick={() => navigate('/login')}>
              <span>Se connecter</span>
            </button>
          </div>
          
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Entreprises</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">1000+</div>
              <div className="stat-label">Boutiques</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">99.9%</div>
              <div className="stat-label">Uptime</div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section - Redesign */}
      <section className="advantages-section" id="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi choisir gestStock ?</h2>
            <p className="section-subtitle">
              Une solution complète pensée pour maximiser votre efficacité
            </p>
          </div>
          <div className="advantages-list">
            {advantages.map((advantage, index) => (
              <div key={index} className="advantage-item">
                <div className="advantage-content">
                  <div className="advantage-icon">{advantage.icon}</div>
                  <div className="advantage-text">
                    <h3>{advantage.title}</h3>
                    <p>{advantage.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Redesign */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Fonctionnalités principales</h2>
            <p className="section-subtitle">
              Des outils puissants pour gérer votre activité efficacement
            </p>
          </div>
          <div className="features-list">
            <div className="feature-row">
              <div className="feature-box">
                <div className="feature-icon">📦</div>
                <h3>Gestion des stocks</h3>
                <p>Suivez vos entrées, sorties et inventaires en temps réel avec alertes automatiques.</p>
              </div>
              <div className="feature-box">
                <div className="feature-icon">💰</div>
                <h3>Point de vente (POS)</h3>
                <p>Interface caisse intuitive avec multiples modes de paiement.</p>
              </div>
              <div className="feature-box">
                <div className="feature-icon">📄</div>
                <h3>Facturation automatique</h3>
                <p>Génération de factures PDF professionnelles avec votre logo.</p>
              </div>
            </div>
            <div className="feature-row">
              <div className="feature-box">
                <div className="feature-icon">👥</div>
                <h3>Gestion multi-utilisateurs</h3>
                <p>Système de rôles complet avec permissions granulaires.</p>
              </div>
              <div className="feature-box">
                <div className="feature-icon">📈</div>
                <h3>Tableaux de bord</h3>
                <p>Statistiques détaillées et graphiques par boutique.</p>
              </div>
              <div className="feature-box">
                <div className="feature-icon">🏢</div>
                <h3>Multi-entreprise</h3>
                <p>Architecture SaaS avec isolation complète des données.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Redesign */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Tarifs simples et transparents</h2>
            <p className="section-subtitle">
              Choisissez le plan qui correspond à vos besoins
            </p>
          </div>
          <div className="pricing-cards">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`price-card ${plan.highlighted ? 'featured' : ''}`}
              >
                {plan.highlighted && <div className="popular-badge">Le plus populaire</div>}
                <div className="price-header">
                  <h3>{plan.name}</h3>
                  <p className="price-desc">{plan.description}</p>
                  <div className="price-amount">
                    <span className="amount">{plan.price}</span>
                    <span className="currency">XOF</span>
                    <span className="period">/ {plan.period}</span>
                  </div>
                </div>
                <ul className="price-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <svg className="check-svg" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button 
                  className={`price-btn ${plan.highlighted ? 'primary' : 'secondary'}`}
                  onClick={() => navigate('/register')}
                >
                  Commencer
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" id="about">
        <div className="cta-content">
          <div className="cta-left">
            <h2 className="cta-title">Prêt à révolutionner votre gestion ?</h2>
            <p className="cta-text">
              Rejoignez plus de 500 entreprises qui ont déjà fait le choix de l'excellence
            </p>
            <ul className="cta-features">
              <li><span className="check">✓</span> Installation en 5 minutes</li>
              <li><span className="check">✓</span> Migration de données gratuite</li>
              <li><span className="check">✓</span> Formation personnalisée incluse</li>
              <li><span className="check">✓</span> Support dédié 24/7</li>
            </ul>
            <button className="btn-cta" onClick={() => navigate('/register')}>
              <span>Commencer maintenant</span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
          <div className="cta-right">
            <div className="cta-card">
              <div className="cta-icon">🎯</div>
              <h3>Essai gratuit</h3>
              <p>30 jours sans carte bancaire</p>
            </div>
            <div className="cta-card">
              <div className="cta-icon">⚡</div>
              <h3>Configuration rapide</h3>
              <p>Prêt en 5 minutes</p>
            </div>
            <div className="cta-card">
              <div className="cta-icon">🔒</div>
              <h3>100% sécurisé</h3>
              <p>Vos données protégées</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>gestStock</h4>
              <p>Solution complète de gestion de stock et ventes pour les entreprises modernes.</p>
            </div>
            <div className="footer-section">
              <h4>Produit</h4>
              <ul>
                <li><a href="#features">Fonctionnalités</a></li>
                <li><a href="#pricing">Tarifs</a></li>
                <li><a href="#demo">Démo</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <ul>
                <li><a href="#help">Centre d'aide</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><a href="#docs">Documentation</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Légal</h4>
              <ul>
                <li><a href="#privacy">Confidentialité</a></li>
                <li><a href="#terms">Conditions</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 gestStock. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
